// Chat style + response-length + text-formatting helpers shared by ChatView
// and its subcomponents.

// 7 thematic chat styles. NSFW is handled at the character level — not here.
// Style is OPTIONAL: users can leave it unset ("Pick a style") or change any
// time during the story from the Style tab.
export const STYLE_OPTIONS = [
  { id: "fluff",     title: "Fluff",             desc: "Cozy, warm, low-stakes moments." },
  { id: "slowburn",  title: "Slow-burn Romance", desc: "Longing, tension, tender pacing." },
  { id: "drama",     title: "Angst / Drama",     desc: "Emotional stakes, conflict, catharsis." },
  { id: "comedy",    title: "Comedy",            desc: "Playful, witty, banter-forward." },
  { id: "adventure", title: "Adventure",         desc: "Action, quests, high energy." },
  { id: "dark",      title: "Dark / Thriller",   desc: "Suspense, danger, edge (no explicit horror)." },
  { id: "grounded",  title: "Grounded",          desc: "Serious, weighty, character-driven realism." },
];
export const STYLE_LABELS = Object.fromEntries(STYLE_OPTIONS.map((s) => [s.id, s.title]));

export const LENGTH_OPTIONS = [
  { id: "concise",   title: "Concise",   desc: "~1–2 short paragraphs." },
  { id: "balanced",  title: "Balanced",  desc: "~2–4 paragraphs. (Default)" },
  { id: "immersive", title: "Immersive", desc: "~4–7 rich, novelistic paragraphs." },
];
export const LENGTH_LABELS = Object.fromEntries(LENGTH_OPTIONS.map((l) => [l.id, l.title]));

// "resets Mar 1" — the date the monthly recap counter rolls over.
export function formatMonthReset() {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return next.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Inline formatting that mirrors what our export renderers produce:
//   ***text*** → bold italic
//   **text**   → bold
//   *text*     → italic (any length now — the LLM writes actions like
//                 *she smiles softly at him* which users legitimately want italic)
//   __text__   → underline
//   A line of only '---' / '----' / '— — —' etc. → centered scene break
//   (Also matches iOS auto-corrected em-dash/en-dash so mobile users get the
//    scene break they expect after typing '---'.)
export function renderContent(text) {
  // 1. Escape HTML.
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // 2. Split on scene-break lines. Matches:
  //    - 3+ hyphens: `---`, `----`, `- - -`, `- - - -`
  //    - 1+ em-dashes / en-dashes on a line (iOS autocorrect converts `--` → `–`
  //      and `---` → `—`; someone typing `----` may end up with `—` or `——`
  //      after the OS chomps it, so a single em-dash line should also break)
  //    - Whitespace between chars OK
  const SCENE_RE = /^\s*(?:-\s*){3,}\s*$|^\s*[\u2013\u2014][\u2013\u2014\s]*$/;
  const blocks = escaped.split("\n").reduce((acc, line) => {
    if (SCENE_RE.test(line)) { acc.push({ break: true }); acc.push({ text: "" }); }
    else {
      const last = acc[acc.length - 1];
      if (last && !last.break) last.text += (last.text ? "\n" : "") + line;
      else acc.push({ text: line });
    }
    return acc;
  }, [{ text: "" }]);

  return blocks.map((b) => {
    if (b.break) return `<div class="scene-break-inline">* * *</div>`;
    let s = b.text;
    // Bold italic ***
    s = s.replace(/\*\*\*([^*\n][^*\n]*?)\*\*\*/g, "<strong><em>$1</em></strong>");
    // Bold **
    s = s.replace(/\*\*([^*\n][^*\n]*?)\*\*/g, "<strong>$1</strong>");
    // Italic * — any inline span, but requires content on both sides of the *s
    // (a stray leading * won't italicize the rest of the message).
    s = s.replace(/\*([^*\n]+?)\*/g, "<em>$1</em>");
    // Underline __text__
    s = s.replace(/__([^_\n]+?)__/g, "<u>$1</u>");
    return s.replace(/\n/g, "<br/>");
  }).join("");
}
